import { useCallback, useEffect, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FilterListRoundedIcon } from "@l2r-front/l2r-icons"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworksDispatchContext } from "@l2r-front/l2r-networks"
import { SidebarContent, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { EquipmentSidebarComponents } from "../../component/EquipmentSidebarComponents"
import { useEquipmentStateContext } from "../../contexts/EquipmentContext"

import { TagsFilterList } from "../TagsFilterList"

import * as Styled from "./EquipmentSidebar.styled"

export function EquipmentSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const { ascendantCodes, descendantCodes } = useEquipmentStateContext()
    const isMobile = useIsMobileDevice()

    const { getMapRef } = useMapDispatchContext()
    const { road } = useParams()
    const { setRoadSearched } = useNetworksDispatchContext()
    const navigate = useNavigateWithSearchParams()
    const [displayFilters, setDisplayFilters] = useState(false)

    const toggleFilters = useCallback(() => {
        return setDisplayFilters(previousState => !previousState)
    }, [])

    const additionalSidebars = useMemo(() => {
        if (isMobile) {
            return null
        }

        return [...ascendantCodes]
            .reverse()
            .filter((_, index) => ((index % 2 === 1) && ((ascendantCodes.length > index + 1) || descendantCodes.length > 1)))
            .map((code, index) => (
                <Styled.AdditionalSidebar key={code} index={index} >
                    <Styled.EquipmentTypeList baseEquipmentTypeCode={code} />
                </Styled.AdditionalSidebar>
            ))
    }, [ascendantCodes, descendantCodes, isMobile])

    const filterComponents = useMemo(() => {
        return (
            <Styled.TitleSideComponentWrapper>
                {!isMobile && <Styled.IconButton onClick={toggleFilters}>
                    <FilterListRoundedIcon color="cta-bg/cta-bg-primary" id="incidents-filters-button" />
                </Styled.IconButton>}
            </Styled.TitleSideComponentWrapper>
        )
    }, [isMobile, toggleFilters])

    const navigateBack = useCallback(() => {
        setRoadSearched(null)
        navigate("..")
    }, [navigate, setRoadSearched])

    useEffect(() => {
        const map = getMapRef()?.getMap()
        if (map?.style) {
            map?.resize()
        }
    }, [ascendantCodes, getMapRef])

    return (
        <SidebarContent
            id="equipment-sidebar"
            onBack={road ? navigateBack : null}
            title={t(I18N_NAMESPACE, `containers.equipmentSidebar.${road ? "roadTitle" : "title"}`)}
            titleSideComponent={filterComponents}
        >
            <Styled.TagsFilterListWrapper>
                <TagsFilterList />
            </Styled.TagsFilterListWrapper>
            <Styled.Container>
                <Styled.EquipmentFilters displayFilters={displayFilters} close={toggleFilters} />
                <EquipmentSidebarComponents />
            </Styled.Container>
            {additionalSidebars}
        </SidebarContent>
    )
}